import React, { useState } from "react";
import { Button, Flex, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../../component/EntitySelect";
import { TAKS_IMPORT } from "../../../config/lang";
import { upload } from "../../../util/action";
import { translate } from "../../../util/util";
import fieldIcon from "./fieldIcons";

const byId = list => Array.from(new Map(list.map(o => [o.id, o])).values());

/* The antd entity dropdown, not the legacy react-select `EntityDropdown` — one control kit per
 * screen (spec/02).
 *
 * ⚠ The stored value is the full option OBJECT in both modes now (it used to be a bare id for
 * single-value fields). `saveItem` unwraps entity fields to ids before POST/PATCH, and an object
 * can label itself: a bare id makes the select show "52" until it has fetched page 1 to find the
 * name, which is exactly what a just-picked value should never have to do.
 *
 * A multi field may declare `fileImport: { url, accept }` in its descriptor: an Upload button
 * under the select posts the file to that endpoint and MERGES the returned `valid_ids` (already
 * `{id, name}` objects — the option shape) into the value. This is how `/point_groups` bulk-adds
 * points from CSV/XLSX on create AND edit (PICSELL-1581 #18/#19); the endpoint's own error rows
 * are surfaced, and nothing already picked is dropped.
 */
export default function EntityField({ field, value, onChange, item = {}, lang }) {
  const [importing, setImporting] = useState(false);

  const select = (
    <EntitySelect
      entity={field.entity}
      value={value}
      isMulti={!!field.isMulti}
      /* County scope, applied by EntitySelect for point entities only. */
      county={field.scopeBy ? item[field.scopeBy] : undefined}
      /* Create/edit pickers offer ACTIVE related objects only — a deactivated one cannot be
         saved. A descriptor overrides via `field.query`. */
      extraQuery={{ active: true, ...(field.query || {}) }}
      prefix={fieldIcon(field)}
      /* `tall` shows every chip in a wrapping, scrollable area; a plain wide multi shows as many as
         fit on one line then `+N`; a normal-width one keeps the single-chip `+N`. */
      tall={!!field.tall}
      maxTagCount={field.tall ? undefined : field.isMulti && field.wide ? "responsive" : 1}
      placeholder={field.placeholder}
      ariaLabel={field._label}
      onChange={option => onChange(field.name, field.isMulti ? option || [] : option || null)}
    />
  );

  if (!field.fileImport || !field.isMulti) return select;

  const t = key => translate(TAKS_IMPORT, key, lang);

  const importFile = file => {
    const form = new FormData();
    form.append("file", file);
    setImporting(true);
    upload({ method: "POST", url: field.fileImport.url, post: form }, response => {
      setImporting(false);
      if (response?.errors?.length) Alert.error(`${t("file_error_uploaded")} ( ${response.errors[0]?.reason} )`, { position: "top-right" });
      if (!Array.isArray(response?.valid_ids)) return;
      onChange(field.name, byId([...(value || []), ...response.valid_ids]));
      Alert.success(t("file_successfully_uploaded"), { position: "top-right" });
    });
    return false; // never let antd upload it itself
  };

  return (
    /* Default stretch, so the select keeps the full cell width it takes from its parent. */
    <Flex vertical gap={8}>
      {select}
      <Upload accept={field.fileImport.accept || ".csv,.xlsx"} showUploadList={false} beforeUpload={importFile}>
        <Button size="small" icon={<UploadOutlined />} loading={importing}>
          {t("group_import_btn")}
        </Button>
      </Upload>
    </Flex>
  );
}
